export const MESSAGE_STATUSES = [
  "queued",
  "sending",
  "sent",
  "delivered",
  "bounced",
  "complained",
  "failed",
  "skipped"
] as const
export type MessageStatus = (typeof MESSAGE_STATUSES)[number]

export const QUICK_FILTERS = [
  "all",
  "email",
  "sms",
  "automated",
  "replied",
  "failed"
] as const
export type QuickFilter = (typeof QUICK_FILTERS)[number]

export const MESSAGE_RECORD_TYPES = [
  "lead",
  "project",
  "customer",
  "subcontractor",
  "supplier"
] as const
export type MessageRecordType = (typeof MESSAGE_RECORD_TYPES)[number]

export interface RecordRef {
  type: MessageRecordType
  id: string
  label?: string
  name?: string
}

export interface LatestReply {
  from_name: string
  from_address: string
  body_preview: string
  received_at: string
  forwarded_to: string[]
}

export const MESSAGE_SOURCES = [
  "manual",
  "campaign",
  "automation",
  "system"
] as const
export type MessageSourceKey = (typeof MESSAGE_SOURCES)[number]

export interface MessageRow {
  id: string
  source?: MessageSourceKey
  channel: "email" | "sms"
  kind: string
  category: string
  source_feature: string
  status: MessageStatus
  status_reason: string
  failure_reason: string
  to_address: string
  subject: string
  contact_kind: string
  customer: string | null
  lead: string | null
  campaign: string | null
  sender: string | null
  sender_name: string | null
  reply_count: number
  created: string
  sent_at: string | null
  delivered_at: string | null
  failed_at: string | null
  record?: RecordRef | null
  contact_name?: string
  body_preview?: string
  latest_reply?: LatestReply | null
  from_name?: string
  from_address?: string
  hold_reason?: string
  not_before?: string | null
  attempt_count?: number
  /** Why a message not sent yet is waiting (a `QueueReason`); "" once it has left the queue. */
  queue_reason?: string
  /** When a waiting message is next tried: its `not_before`, else when it was queued. */
  next_attempt_at?: string | null
  /** The other channel of an Email + SMS automation step. */
  companion?: QueueCompanion | null
  hold_zone?: string | null
  hold_zone_source?: string | null
  /** Which queue action the caller is offered on this message; absent from an older backend. */
  actions?: QueueActionAvailability
}

export interface QueueActionAvailability {
  send_now: boolean
  retry: boolean
}

export type QueueActionKind = "send_now" | "retry"

/** A rule that stops a message leaving; `until`/`zone` for a hold that ends. */
export interface QueueActionBlocker {
  code: string
  until: string | null
  zone: string | null
}

export interface SendNowPreview {
  action: "send_now"
  message_id: string
  status: MessageStatus
  channel: "email" | "sms"
  to_address: string
  queue_reason?: string
  hold_reason: string
  not_before: string | null
  hold_zone: string | null
  hold_zone_source: string | null
  /** The time someone booked it for, when sending now is earlier than planned. */
  scheduled_for: string | null
  /** What the message already is when it no longer waits (e.g. `already_sent`). */
  state: string | null
  blockers: QueueActionBlocker[]
  override: { needed: boolean; reasons: string[]; allowed: boolean }
  can_send: boolean
}

export interface RetryPreview {
  action: "retry"
  message_id: string
  status: MessageStatus
  channel: "email" | "sms"
  to_address: string
  failure_reason: string
  status_reason: string
  attempt_count: number
  state: string | null
  /** The provider may have accepted it; resending could deliver it twice. */
  uncertain: boolean
  not_retryable_reason:
    | ""
    | "not_failed"
    | "managed_by_feature"
    | "provider_accepted"
  blockers: QueueActionBlocker[]
  waits: { hold_reason: string; until: string; hold_zone: string | null } | null
  can_retry: boolean
}

export const QUEUE_ACTION_OUTCOMES = [
  "processing",
  "already_sent",
  "in_progress",
  "failed",
  "skipped",
  "blocked",
  "override_required",
  "confirmation_required",
  "not_retryable"
] as const
export type QueueActionOutcome = (typeof QUEUE_ACTION_OUTCOMES)[number]

export interface QueueActionResult {
  outcome: QueueActionOutcome
  changed: boolean
  message: MessageDetail
  preview?: SendNowPreview | RetryPreview
}

export interface QueueActionRecord {
  action: QueueActionKind
  at: string
  by: string | null
  override: string[]
  scheduled_for: string | null
  confirmed_not_received: boolean
}

/** Every reason the server gives for a message not sent yet (`queue_reason`). */
export const QUEUE_REASONS = [
  "quiet_hours",
  "send_window",
  "legal_hours",
  "scheduled",
  "retry",
  "throttled",
  "capped",
  "paused",
  "due",
  "sending"
] as const
export type QueueReason = (typeof QUEUE_REASONS)[number]

export interface QueueCompanion {
  channel: "email" | "sms"
  status: MessageStatus | "cancelled"
  message_id: string | null
}

export interface ThreadAnswer {
  id: string
  channel: "email" | "sms"
  status: string
  body_text: string
  sender_name: string | null
  created: string
  sent_at: string | null
}

export interface Reply {
  id: string
  channel: "email" | "sms"
  from_name: string
  from_address: string
  subject: string
  body_text: string
  received_at: string
  answers?: ThreadAnswer[]
}

export interface MessageDetail extends Omit<MessageRow, "campaign" | "record"> {
  hold_reason: string
  not_before: string | null
  /** The zone `not_before` is read in for a quiet-hours, send-window or legal-hours hold. */
  hold_zone?: string | null
  /** Where that zone came from: `recipient`, `company` (fallback) or `us_zones`. */
  hold_zone_source?: string | null
  /** Why no time rule applies: `transactional_email`, `requested_code`, or "". */
  exempt?: string
  from_name: string
  from_address: string
  body_html: string
  body_text: string
  record: RecordRef | null
  first_replied_at: string | null
  replies: Reply[]
  skipped_at: string | null
  campaign?: string | null
  campaign_subject?: string | null
  automation?: {
    id: string
    name: string
    category: string
    deleted?: boolean
  } | null
  bounced_at?: string | null
  complained_at?: string | null
  clicked_at?: string | null
  /** Who asked to send it now or again, when, and with which override. */
  queue_actions?: QueueActionRecord[]
}

export type MessageFilter = Record<string, string>

export interface OverviewKpi {
  key: "sent" | "delivered" | "replied" | "failed"
  value: number
  previous: number
  filter: MessageFilter
}

export interface NeedsAttentionItem {
  key:
    | "bounced"
    | "failed_sms"
    | "opted_out"
    | "sms_registration_pending"
    | "auto_paused"
  count: number | null
  /** On `opted_out`: the distinct contacts behind the opt-outs `count` counts. */
  contacts?: number | null
  filter: MessageFilter | null
}

export interface OverviewCost {
  email: number | null
  sms: number | null
  currency: string
}

export interface OverviewRates {
  bounce: number | null
  complaint: number | null
  opt_out: number | null
}

export interface OverviewSmsNumber {
  number: string
  sent: number
  delivered: number
  failed: number
  segments: number
}

export interface Overview {
  period: "7d" | "30d" | "90d"
  window: { created_after: string; created_before: string }
  kpis: OverviewKpi[]
  by_day: Array<{ day: string; channel: "email" | "sms"; count: number }>
  channel_split: Record<"email" | "sms", number>
  needs_attention: NeedsAttentionItem[]
  recent: MessageRow[]
  cost?: OverviewCost
  rates?: OverviewRates
  sms_number?: OverviewSmsNumber | null
  active_campaigns: Array<{
    id: string
    subject: string
    channel: string
    status: string
    scheduled_for: string | null
  }>
  automations: { enabled: number; total: number }
  upcoming?: OverviewUpcoming
  identity: {
    email_address: string
    display_name: string
    custom_domain: string | null
    domain_state: string
    status: "active" | "paused"
    paused_reason: string | null
    sms_number: string | null
  } | null
}

export interface OverviewUpcoming {
  scheduled_count: number
  campaigns: Array<{
    id: string
    name?: string
    subject: string
    channel: "email" | "sms"
    status: string
    scheduled_for: string | null
    recipient_count: number
  }>
  automations: Array<{
    automation_id: string
    name: string
    category: string | null
    channel: string | null
    next_at: string
    pending_count: number
    /** Nurturing runs due; their recipients are only known when they run. */
    pending_runs?: number
  }> | null
}
